import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  ConnectionMode,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  reconnectEdge,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type OnConnectEnd,
  type OnReconnect,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { ApiFehler, hole, schicke } from '../lib/api'
import { Button, Switch, Symbol, useSnackbar } from '../ui'
import { AutomationEditorContext } from './automation-editor/editor-context'
import { automationToGraph, graphToAutomation } from './automation-editor/graph-mapping'
import { EditableEdge } from './automation-editor/edges/EditableEdge'
import { ActionNode } from './automation-editor/nodes/ActionNode'
import { ConditionNode } from './automation-editor/nodes/ConditionNode'
import { TriggerNode } from './automation-editor/nodes/TriggerNode'
import type { AutomationPayload } from './automation-editor/types'

const nodeTypes = {
  trigger: TriggerNode,
  condition: ConditionNode,
  action: ActionNode,
}

const edgeTypes = {
  editable: EditableEdge,
}

interface ServerAutomation {
  id: string
  name: string
  triggerEvent: string
  conditions: Record<string, unknown>
  actions: Array<Record<string, unknown>>
  enabled: boolean
}

function EditorInhalt() {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const zeigeMeldung = useSnackbar()

  const [name, setName] = useState('Neue Automatisierung')
  const [enabled, setEnabled] = useState(true)
  const [nodes, setNodes] = useState<Node[]>([])
  const [edges, setEdges] = useState<Edge[]>([])
  const [geladen, setGeladen] = useState(false)
  const [connectingNodeId, setConnectingNodeId] = useState<string | null>(null)

  const nodesRef = useRef(nodes)
  nodesRef.current = nodes

  const connectingFromRef = useRef<{
    nodeId: string
    handleId: string | null
    handleType: string | null
  } | null>(null)

  // Bestehende Automatisierungen laden, falls eine ID übergeben wurde
  const { data: automationsData, isPending } = useQuery({
    queryKey: ['automations'],
    queryFn: () => hole<{ automations: ServerAutomation[] }>('/api/automations'),
    enabled: Boolean(id),
  })

  useEffect(() => {
    if (id && automationsData && !geladen) {
      const auto = automationsData.automations.find((a) => a.id === id)
      if (auto) {
        setName(auto.name)
        setEnabled(auto.enabled)
        const g = automationToGraph(auto)
        setNodes(g.nodes)
        setEdges(g.edges)
        setGeladen(true)
      }
    } else if (!id && !geladen) {
      const g = automationToGraph({
        name: 'Neue Automatisierung',
        triggerEvent: 'tasks.completed',
        conditions: { kind: 'chore' },
        actions: [{ type: 'allowance.bonus', recipient: 'actor', amountCents: 50 }],
        enabled: true,
      })
      setNodes(g.nodes)
      setEdges(g.edges)
      setGeladen(true)
    }
  }, [id, automationsData, geladen])

  // Rerender-Signale von Nodes verarbeiten
  useEffect(() => {
    const onNodeDataChange = () => {
      setNodes((current) => [...current])
    }
    window.addEventListener('node-data-change', onNodeDataChange)
    return () => window.removeEventListener('node-data-change', onNodeDataChange)
  }, [])

  const onNodesChange = useCallback(
    (changes: NodeChange<Node>[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  )

  const onEdgesChange = useCallback(
    (changes: EdgeChange<Edge>[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  )

  // Robuste Verknüpfungs-Funktion für alle Wege (Drag-and-Drop, Klick, Node-Drop, Dropdown)
  const connectNodes = useCallback(
    (fromId: string, toId: string) => {
      if (fromId === toId) return

      const currentNodes = nodesRef.current
      const fromNode = currentNodes.find((n) => n.id === fromId)
      const toNode = currentNodes.find((n) => n.id === toId)
      if (!fromNode || !toNode) return

      const order: Record<string, number> = {
        trigger: 0,
        condition: 1,
        action: 2,
      }

      const fromRank = order[fromNode.type ?? ''] ?? 0
      const toRank = order[toNode.type ?? ''] ?? 0

      // Zwei gleiche Typen wie zwei Trigger oder zwei Aktionen nicht verbinden
      if (fromNode.type === 'trigger' && toNode.type === 'trigger') return
      if (fromNode.type === 'action' && toNode.type === 'action') return

      let source = fromId
      let sourceHandle = fromNode.type === 'trigger' ? 'trigger-out' : 'cond-out'
      let target = toId
      let targetHandle = toNode.type === 'condition' ? 'cond-in' : 'action-in'

      // Falls rückwärts gezogen (z.B. Aktion -> Bedingung), umkehren
      if (fromRank > toRank) {
        source = toId
        sourceHandle = toNode.type === 'trigger' ? 'trigger-out' : 'cond-out'
        target = fromId
        targetHandle = fromNode.type === 'condition' ? 'cond-in' : 'action-in'
      }

      setEdges((eds) => {
        if (eds.some((e) => e.source === source && e.target === target)) {
          return eds
        }
        return addEdge(
          {
            id: `edge-${source}-${target}`,
            source,
            sourceHandle,
            target,
            targetHandle,
            animated: true,
          },
          eds,
        )
      })

      setConnectingNodeId(null)
      connectingFromRef.current = null
      zeigeMeldung('Bausteine erfolgreich verknüpft', 'erfolg')
    },
    [zeigeMeldung],
  )

  const disconnectEdge = useCallback(
    (edgeId: string) => {
      setEdges((eds) => eds.filter((e) => e.id !== edgeId))
      zeigeMeldung('Verbindung getrennt', 'info')
    },
    [zeigeMeldung],
  )

  const disconnectBetween = useCallback(
    (nodeA: string, nodeB: string) => {
      setEdges((eds) =>
        eds.filter(
          (e) =>
            !(
              (e.source === nodeA && e.target === nodeB) ||
              (e.source === nodeB && e.target === nodeA)
            ),
        ),
      )
      zeigeMeldung('Verbindung getrennt', 'info')
    },
    [zeigeMeldung],
  )

  const removeNode = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((candidate) => candidate.id === nodeId)
      if (!node || node.type === 'trigger') return

      setNodes((current) => current.filter((candidate) => candidate.id !== nodeId))
      setEdges((current) =>
        current.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
      )
      if (connectingFromRef.current?.nodeId === nodeId) {
        connectingFromRef.current = null
        setConnectingNodeId(null)
      }
      zeigeMeldung('Baustein und zugehörige Verbindungen entfernt', 'info')
    },
    [zeigeMeldung],
  )

  const onReconnect: OnReconnect = useCallback(
    (oldEdge, connection) => {
      if (!connection.source || !connection.target || connection.source === connection.target) {
        return
      }

      setEdges((current) => reconnectEdge(oldEdge, connection, current))
      zeigeMeldung('Verbindung erfolgreich geändert', 'erfolg')
    },
    [zeigeMeldung],
  )

  const startConnecting = useCallback(
    (nodeId: string) => {
      connectingFromRef.current = { nodeId, handleId: null, handleType: 'source' }
      setConnectingNodeId(nodeId)
      zeigeMeldung('Klicke auf den nächsten Baustein, um die beiden zu verknüpfen', 'info')
    },
    [zeigeMeldung],
  )

  const cancelConnecting = useCallback(() => {
    connectingFromRef.current = null
    setConnectingNodeId(null)
  }, [])

  const onConnect = useCallback(
    (connection: Connection) => {
      if (connection.source && connection.target) {
        connectNodes(connection.source, connection.target)
      }
    },
    [connectNodes],
  )

  // Start einer Verbindung merken (sowohl Drag als auch Klick)
  const onConnectStart = useCallback(
    (
      _event: unknown,
      params: { nodeId: string | null; handleId: string | null; handleType: string | null },
    ) => {
      if (params.nodeId) {
        connectingFromRef.current = {
          nodeId: params.nodeId,
          handleId: params.handleId,
          handleType: params.handleType,
        }
        setConnectingNodeId(params.nodeId)
      }
    },
    [],
  )

  // Ermöglicht das Loslassen einer Verbindungslinie ÜBERALL auf der Ziel-Karte (Node-Level Drop)
  const onConnectEnd: OnConnectEnd = useCallback(
    (event) => {
      const fromNodeId = connectingFromRef.current?.nodeId

      const clientX = 'clientX' in event ? event.clientX : event.changedTouches[0]?.clientX
      const clientY = 'clientY' in event ? event.clientY : event.changedTouches[0]?.clientY

      if (clientX != null && clientY != null && fromNodeId) {
        const dropTarget = document.elementFromPoint(clientX, clientY)
        const nodeEl = dropTarget?.closest('.editor-node') as HTMLElement | null
        if (nodeEl) {
          const targetNodeId =
            nodeEl.getAttribute('data-id') ??
            nodeEl.closest('.react-flow__node')?.getAttribute('data-id')

          if (targetNodeId && targetNodeId !== fromNodeId) {
            connectNodes(fromNodeId, targetNodeId)
            return
          }
        }
      }

      // Falls nicht verbunden, nach kleinem Timeout (damit Click-Events nicht ins Leere laufen) zurücksetzen
      setTimeout(() => {
        connectingFromRef.current = null
        setConnectingNodeId(null)
      }, 150)
    },
    [connectNodes],
  )

  // Ermöglicht das Anklicken einer Ziel-Karte zum Fertigstellen einer Verbindung
  const onNodeClick = useCallback(
    (_: React.MouseEvent, clickedNode: Node) => {
      if (connectingNodeId && connectingNodeId !== clickedNode.id) {
        connectNodes(connectingNodeId, clickedNode.id)
      }
    },
    [connectingNodeId, connectNodes],
  )

  const onPaneClick = useCallback(() => {
    if (connectingNodeId) {
      cancelConnecting()
    }
  }, [connectingNodeId, cancelConnecting])

  const addConditionNode = () => {
    const newId = `node-condition-${Date.now()}`
    const newNode: Node = {
      id: newId,
      type: 'condition',
      position: { x: 440, y: 100 + nodes.length * 40 },
      data: { key: 'kind', value: 'chore' },
    }
    setNodes((nds) => [...nds, newNode])

    // Automatisch mit dem Trigger verbinden, falls vorhanden
    const trigger = nodes.find((n) => n.type === 'trigger')
    if (trigger) {
      setEdges((eds) =>
        addEdge(
          {
            id: `edge-${trigger.id}-${newId}`,
            source: trigger.id,
            sourceHandle: 'trigger-out',
            target: newId,
            targetHandle: 'cond-in',
            animated: true,
          },
          eds,
        ),
      )
    }
    zeigeMeldung('Bedingungs-Knoten hinzugefügt und verbunden', 'info')
  }

  const addActionNode = () => {
    const newId = `node-action-${Date.now()}`
    const newNode: Node = {
      id: newId,
      type: 'action',
      position: { x: 820, y: 100 + nodes.length * 40 },
      data: { type: 'notification.send', recipients: 'guardians' },
    }
    setNodes((nds) => [...nds, newNode])

    // Automatisch mit der letzten Bedingung (oder dem Trigger) verbinden
    const lastCondition = [...nodes].reverse().find((n) => n.type === 'condition')
    const trigger = nodes.find((n) => n.type === 'trigger')
    const sourceNode = lastCondition ?? trigger
    if (sourceNode) {
      const sourceHandle = sourceNode.type === 'trigger' ? 'trigger-out' : 'cond-out'
      setEdges((eds) =>
        addEdge(
          {
            id: `edge-${sourceNode.id}-${newId}`,
            source: sourceNode.id,
            sourceHandle,
            target: newId,
            targetHandle: 'action-in',
            animated: true,
          },
          eds,
        ),
      )
    }
    zeigeMeldung('Aktions-Knoten hinzugefügt und verbunden', 'info')
  }

  const autoConnectAll = () => {
    const currentNodes = nodesRef.current
    const trigger = currentNodes.find((n) => n.type === 'trigger')
    const conditions = currentNodes.filter((n) => n.type === 'condition')
    const actions = currentNodes.filter((n) => n.type === 'action')

    if (!trigger || actions.length === 0) {
      zeigeMeldung('Mindestens ein Auslöser und eine Aktion erforderlich', 'error')
      return
    }

    const newEdges: Edge[] = []
    if (conditions.length > 0) {
      for (const cond of conditions) {
        newEdges.push({
          id: `edge-${trigger.id}-${cond.id}`,
          source: trigger.id,
          sourceHandle: 'trigger-out',
          target: cond.id,
          targetHandle: 'cond-in',
          animated: true,
        })
        for (const act of actions) {
          newEdges.push({
            id: `edge-${cond.id}-${act.id}`,
            source: cond.id,
            sourceHandle: 'cond-out',
            target: act.id,
            targetHandle: 'action-in',
            animated: true,
          })
        }
      }
    } else {
      for (const act of actions) {
        newEdges.push({
          id: `edge-${trigger.id}-${act.id}`,
          source: trigger.id,
          sourceHandle: 'trigger-out',
          target: act.id,
          targetHandle: 'action-in',
          animated: true,
        })
      }
    }

    setEdges(newEdges)
    zeigeMeldung('Alle Bausteine automatisch verknüpft', 'erfolg')
  }

  const saveMutation = useMutation({
    mutationFn: (payload: AutomationPayload) => {
      if (id) {
        return schicke<{ automation: ServerAutomation }>('PATCH', `/api/automations/${id}`, payload)
      }
      return schicke<{ automation: ServerAutomation }>('POST', '/api/automations', payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['automations'] })
      zeigeMeldung(
        id ? 'Automatisierung erfolgreich aktualisiert' : 'Automatisierung erfolgreich erstellt',
        'erfolg',
      )
      void navigate('/automatisierungen')
    },
    onError: (err) => {
      const msg = err instanceof ApiFehler ? err.message : 'Fehler beim Speichern'
      zeigeMeldung(msg, 'error')
    },
  })

  const handleSpeichern = () => {
    const ergebnis = graphToAutomation(nodes, edges, { name, enabled })
    if (!ergebnis.success) {
      zeigeMeldung(ergebnis.fehler, 'error')
      return
    }

    saveMutation.mutate(ergebnis.automation)
  }

  if (id && isPending) {
    return (
      <div className="stapel">
        <p className="unterzeile">Automatisierung wird geladen …</p>
      </div>
    )
  }

  return (
    <AutomationEditorContext.Provider
      value={{
        nodes,
        edges,
        connectingNodeId,
        startConnecting,
        cancelConnecting,
        connectNodes,
        disconnectEdge,
        disconnectBetween,
        removeNode,
      }}
    >
      <div className="editor-container">
        {/* Editor-Kopfzeile */}
        <div className="editor-leiste">
          <div className="editor-leiste__links">
            <Link
              to="/automatisierungen"
              className="btn btn--text"
              title="Zurück zu den Automatisierungen"
            >
              <Symbol name="chevron-left" width={20} height={20} />
              <span>Zurück</span>
            </Link>
            <input
              type="text"
              className="editor-leiste__titel-input"
              value={name}
              placeholder="Name der Regel"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="editor-leiste__rechts">
            <Switch
              label={enabled ? 'Aktiv' : 'Pausiert'}
              checked={enabled}
              onChange={(naechster) => setEnabled(naechster)}
            />
            <Button variant="tonal" onClick={addConditionNode} title="Neue Bedingung hinzufügen">
              + Bedingung
            </Button>
            <Button variant="tonal" onClick={addActionNode} title="Neue Aktion hinzufügen">
              + Aktion
            </Button>
            <Button
              variant="text"
              onClick={autoConnectAll}
              title="Alle Bausteine in logischer Reihenfolge automatisch miteinander verbinden"
            >
              Neu verknüpfen
            </Button>
            <Button variant="filled" onClick={handleSpeichern} disabled={saveMutation.isPending}>
              Speichern
            </Button>
          </div>
        </div>

        {/* React Flow Arbeitsfläche */}
        <div className="editor-arbeitsflaeche">
          {connectingNodeId && (
            <div className="editor-hinweis-banner">
              <span>
                Baustein ausgewählt. Klicke auf den nächsten Baustein, um die beiden zu verknüpfen.
              </span>
              <button
                type="button"
                className="btn btn--tonal editor-hinweis-banner__btn"
                onClick={cancelConnecting}
              >
                Abbrechen
              </button>
            </div>
          )}

          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={onConnectEnd}
            onReconnect={onReconnect}
            edgesReconnectable
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            connectionMode={ConnectionMode.Loose}
            connectionRadius={60}
            connectOnClick={true}
            isValidConnection={(c) => c.source !== c.target}
            defaultEdgeOptions={{ animated: true, type: 'editable' }}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            className="editor-canvas"
          >
            <Background gap={16} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
      </div>
    </AutomationEditorContext.Provider>
  )
}

export function AutomatisierungEditor() {
  return (
    <ReactFlowProvider>
      <EditorInhalt />
    </ReactFlowProvider>
  )
}
